import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Índice lateral reutilizable para las tabs del detalle de plantilla.
 * items: [{ id, label, conditional, title }] — id = id del elemento al que hace scroll.
 * Estados: hover (gray-100), activo (primary-500, blanco, redondeado).
 *
 * Dos modos:
 *  - SCROLL-SPY (default): el activo lo decide el scroll. Click → scroll a la sección.
 *    Activo DINÁMICO con dos fuentes separadas para que no se pisen:
 *      · Scroll MANUAL → IntersectionObserver marca la sección visible.
 *      · CLIC → fija de inmediato la sección elegida y BLOQUEA el scroll-spy mientras
 *        dura el auto-scroll (si no, las secciones intermedias que cruzan la viewport
 *        robarían el activo y terminaría en la anterior). El bloqueo se libera cuando
 *        el scroll se detiene (debounce) + fallback si el ítem ya estaba visible.
 *  - CONTROLADO (cuando se pasa `onSelect`): el padre manda el activo (`activeId`) y el
 *    click solo llama `onSelect(id)` — sin scroll-spy ni auto-scroll. Lo usa el timeline
 *    de aprendizaje: click en una fecha → muestra el diff de ese evento.
 *
 * CSS en escriturasTemplates.css.
 */
export default function TemplateSideIndex({ items, activeId: controlledActiveId, onSelect }) {
  const controlled = typeof onSelect === 'function';
  const [spyActiveId, setSpyActiveId] = useState(items[0]?.id ?? null);
  const activeId = controlled ? controlledActiveId : spyActiveId;

  // Bloqueo del scroll-spy mientras corre un auto-scroll disparado por clic.
  const clickLockRef = useRef(false);
  const releaseTimerRef = useRef(null);

  const armRelease = (delay) => {
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = setTimeout(() => {
      clickLockRef.current = false;
    }, delay);
  };

  // Clave estable: solo re-observa si cambian los ids (no en cada render por labels nuevos).
  const idsKey = useMemo(() => items.map((i) => i.id).join('|'), [items]);

  useEffect(() => {
    if (controlled) return undefined; // modo controlado: el activo lo manda el padre, sin scroll-spy
    const ids = idsKey ? idsKey.split('|') : [];
    if (ids.length === 0) return undefined;

    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        // Mantenemos el set fresco siempre, pero durante un clic NO movemos el activo.
        entries.forEach((e) => {
          if (e.isIntersecting) visible.add(e.target.id);
          else visible.delete(e.target.id);
        });
        if (clickLockRef.current) return; // auto-scroll por clic: el spy no manda
        // El activo es el primero (en orden del documento) que esté visible.
        const first = ids.find((id) => visible.has(id));
        if (first) setSpyActiveId(first);
      },
      // -65% inferior: una sección se considera "activa" cuando su inicio cruza el tercio superior.
      { rootMargin: '0px 0px -65% 0px', threshold: 0 }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [idsKey, controlled]);

  // Mientras el clic tiene el bloqueo, cada evento de scroll re-arma el release:
  // cuando el scroll deja de moverse (120ms sin eventos), liberamos el spy.
  useEffect(() => {
    const onScroll = () => {
      if (clickLockRef.current) armRelease(120);
    };
    window.addEventListener('scroll', onScroll, true); // capture: cualquier contenedor scrolleable
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    };
  }, []);

  const handleClick = (id) => {
    if (controlled) {
      onSelect(id); // modo controlado: el padre decide qué mostrar; sin scroll
      return;
    }
    clickLockRef.current = true;
    setSpyActiveId(id); // el clic gana: fija el activo y lo deja fijo durante el auto-scroll
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    armRelease(400); // fallback: si el ítem ya estaba visible y no hubo scroll, libera igual
  };

  return (
    <nav className='tpl-side-index'>
      {items.map((it) => (
        <button
          key={it.id}
          type='button'
          className={`tpl-nav-item ${it.changed ? 'is-changed' : ''} ${activeId === it.id ? 'active' : ''} ${it.conditional ? 'is-conditional' : ''}`}
          onClick={() => handleClick(it.id)}
          title={it.title || ''}
        >
          {it.conditional ? '◐ ' : ''}{it.label}
        </button>
      ))}
    </nav>
  );
}
